import { useEffect, useState } from "react";
import { Button, Card, EmptyState } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { CategoryDTO, CategoryTreeNode } from "@wmart/shared-types";
import { CategoryEditDrawer } from "../components/CategoryEditDrawer.js";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VisibleRow = { node: CategoryTreeNode; depth: number; expanded: boolean };

// ---------------------------------------------------------------------------
// Flatten tree → flat list of visible rows
// ---------------------------------------------------------------------------

function flatten(
  nodes: CategoryTreeNode[],
  depth = 0,
  out: VisibleRow[] = [],
  expandedIds: Set<string>,
): VisibleRow[] {
  for (const n of nodes) {
    const expanded = expandedIds.has(n.id);
    out.push({ node: n, depth, expanded });
    if (expanded) flatten(n.children, depth + 1, out, expandedIds);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Row sub-component (one <li>, wired to useSortable)
// ---------------------------------------------------------------------------

interface RowProps {
  row: VisibleRow;
  onToggleExpand: (id: string) => void;
  onEdit: (node: CategoryTreeNode) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (node: CategoryTreeNode) => void;
}

function Row({ row, onToggleExpand, onEdit, onAddChild, onDelete }: RowProps) {
  const { node, depth, expanded: isOpen } = row;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  // Inline equivalent of CSS.Transform.toString from @dnd-kit/utilities
  const transformStr = transform
    ? `translate3d(${transform.x}px, ${transform.y}px, 0) scaleX(${transform.scaleX}) scaleY(${transform.scaleY})`
    : undefined;

  const style: React.CSSProperties = {
    paddingLeft: `${depth * 24 + 16}px`,
    transform: transformStr,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[24px_24px_1fr_auto] gap-3 border-b border-ink-100 px-4 py-2 hover:bg-paper-warm"
    >
      {/* Drag handle — only this element carries the DnD listeners */}
      <span
        aria-hidden
        className="cursor-grab select-none text-ink-300"
        title="Drag to reparent"
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </span>

      {/* Expand / collapse toggle */}
      <button
        type="button"
        onClick={() => onToggleExpand(node.id)}
        aria-label={isOpen ? "Collapse" : "Expand"}
        disabled={node.child_count === 0}
        className="text-ink-500 disabled:opacity-30"
      >
        {node.child_count === 0 ? "·" : isOpen ? "▼" : "▶"}
      </button>

      {/* Title + meta */}
      <div className="flex flex-col">
        <span className="text-body-sm text-ink-900">{node.title}</span>
        <span className="font-mono text-caption text-ink-500">
          {node.slug} · {node.child_count}{" "}
          {node.child_count === 1 ? "child" : "children"} ·{" "}
          {node.direct_product_count}{" "}
          {node.direct_product_count === 1 ? "product" : "products"}
        </span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 text-caption">
        <button
          type="button"
          className="text-accent hover:text-accent-deep"
          onClick={() => onAddChild(node.id)}
        >
          + child
        </button>
        <button
          type="button"
          className="text-ink-700 hover:text-ink-900"
          onClick={() => onEdit(node)}
        >
          edit
        </button>
        <button
          type="button"
          className="text-danger hover:text-danger-deep"
          onClick={() => onDelete(node)}
        >
          delete
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function CategoriesTree() {
  const { notify, notifyProblem } = useToast();
  const [tree, setTree] = useState<CategoryTreeNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<CategoryDTO | null>(null);
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await api.get<{ tree: CategoryTreeNode[] }>(
        "/v1/admin/categories",
      );
      setTree(data.tree);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function deleteCategory(c: CategoryTreeNode) {
    if (!confirm(`Delete category "${c.title}"?`)) return;
    try {
      await api.delete(`/v1/admin/categories/${c.id}`);
      notify("success", "Category deleted");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
    }
  }

  // -------------------------------------------------------------------------
  // DnD sensors
  // -------------------------------------------------------------------------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // -------------------------------------------------------------------------
  // handleDragEnd — v1 scope
  //
  // SUPPORTED:
  //   1. Same-level reorder: active and over share the same parent_id.
  //      Result: swap/insert within their sibling list; positions become
  //      contiguous 0..N-1 within that parent.
  //
  //   2. Cross-level drop: active and over have different depths.
  //      Result: treat as "insert active after over, under over's parent".
  //      Active is reparented to over's parent and placed immediately after
  //      over in the sibling list.
  //
  // NOT SUPPORTED (v1):
  //   - Drop INTO an expandable node (becoming its first child) — too
  //     ambiguous without a distinct "drop-on-centre" affordance.
  //   - Multiple selections.
  //
  // The API guards against cycles; we don't replicate that check here.
  // -------------------------------------------------------------------------

  async function handleDragEnd(e: DragEndEvent) {
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;

    // No valid drop target or dropped on self → bail.
    if (!overId || activeId === overId) return;

    // Rebuild the full flat visible list (same logic as render-time `visible`).
    const rows = flatten(tree, 0, [], expanded);

    const activeIdx = rows.findIndex((r) => r.node.id === activeId);
    const overIdx = rows.findIndex((r) => r.node.id === overId);
    if (activeIdx === -1 || overIdx === -1) return;

    const activeRow = rows[activeIdx]!;
    const overRow = rows[overIdx]!;

    // Determine the target parent for the active node.
    // - Same depth → same parent (same-level reorder).
    // - Different depth → reparent under over's parent.
    const targetParentId = overRow.node.parent_id ?? null;

    // Build the new sibling list under targetParentId after the move.
    // "Sibling list" = visible rows whose parent_id === targetParentId,
    // excluding the active node (it will be re-inserted).
    const siblings = rows
      .filter(
        (r) =>
          r.node.id !== activeId &&
          (r.node.parent_id ?? null) === targetParentId,
      )
      .map((r) => r.node);

    // Find insertion index: insert active after the over node within siblings.
    const overSibIdx = siblings.findIndex((n) => n.id === overId);
    const insertAt = overSibIdx === -1 ? siblings.length : overSibIdx + 1;

    const reordered = [
      ...siblings.slice(0, insertAt),
      activeRow.node,
      ...siblings.slice(insertAt),
    ];

    // Compute deltas: only nodes whose (parent_id, position) changed.
    type Delta = { id: string; parent_id: string | null; position: number };
    const deltas: Delta[] = [];

    reordered.forEach((node, newPosition) => {
      const originalParent = node.parent_id ?? null;
      const isActive = node.id === activeId;

      // Determine original position within its original sibling list.
      const originalSiblings = rows
        .filter(
          (r) =>
            (r.node.parent_id ?? null) === originalParent &&
            r.node.id !== activeId,
        )
        .map((r) => r.node);

      // For the active node: old position was wherever it sat in its original
      // parent's visible list.
      const oldPosition = isActive
        ? rows
            .filter((r) => (r.node.parent_id ?? null) === originalParent)
            .findIndex((r) => r.node.id === activeId)
        : originalSiblings.findIndex((n) => n.id === node.id);

      const newParent = isActive ? targetParentId : originalParent;
      const parentChanged = isActive && newParent !== originalParent;
      const positionChanged = oldPosition !== newPosition;

      if (parentChanged || positionChanged) {
        deltas.push({ id: node.id, parent_id: newParent, position: newPosition });
      }
    });

    // Cross-level drop: also compact the source parent's remaining siblings.
    // Without this the source parent ends up with a position gap (e.g. [0,2])
    // which causes silent DB drift on subsequent reorders within that parent.
    const isCrossLevel =
      activeRow.node.parent_id !== targetParentId ||
      activeRow.node.parent_id === null !== (targetParentId === null);
    if (isCrossLevel) {
      const srcParentId = activeRow.node.parent_id ?? null;
      const srcSiblings = rows
        .filter(
          (r) =>
            r.node.id !== activeId &&
            (r.node.parent_id ?? null) === srcParentId,
        )
        .map((r) => r.node);

      srcSiblings.forEach((node, newPosition) => {
        const oldPosition = node.position;
        if (oldPosition !== newPosition) {
          // Only push if not already in deltas (same-level logic can't touch these).
          if (!deltas.find((d) => d.id === node.id)) {
            deltas.push({ id: node.id, parent_id: srcParentId, position: newPosition });
          }
        }
      });
    }

    if (deltas.length === 0) return; // Nothing actually changed.

    try {
      await api.patch("/v1/admin/categories/positions", { items: deltas });
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to reorder categories");
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const visible = flatten(tree, 0, [], expanded);

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Categories</h1>
        <Button
          variant="primary"
          onClick={() =>
            setEditing({
              id: "",
              parent_id: null,
              slug: "",
              title: "",
              position: 0,
              created_at: "",
              deleted_at: null,
            })
          }
        >
          New category
        </Button>
      </div>

      {visible.length === 0 && !loading ? (
        <EmptyState
          icon="package"
          title="No categories yet"
          body='Click "New category" to create the first category.'
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-[24px_24px_1fr_auto] gap-3 border-b border-ink-200 bg-paper-warm px-4 py-2 text-caption uppercase tracking-[0.04em] text-ink-500">
            <span aria-hidden></span>
            <span aria-hidden></span>
            <span>Category</span>
            <span>Actions</span>
          </div>

          <DndContext sensors={sensors} onDragEnd={(e) => void handleDragEnd(e)}>
            <SortableContext
              items={visible.map((v) => v.node.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul>
                {visible.map((row) => (
                  <Row
                    key={row.node.id}
                    row={row}
                    onToggleExpand={toggleExpand}
                    onEdit={setEditing}
                    onAddChild={setAddingChildOf}
                    onDelete={(node) => void deleteCategory(node)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </Card>
      )}

      {editing ? (
        <CategoryEditDrawer
          tree={tree}
          category={editing.id ? editing : null}
          prefilledParentId={null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      {addingChildOf ? (
        <CategoryEditDrawer
          tree={tree}
          category={null}
          prefilledParentId={addingChildOf}
          onClose={() => setAddingChildOf(null)}
          onSaved={() => {
            setAddingChildOf(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
